/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { EnvironmentVariableNameValidator, NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  FORM_THEME_LITE,
  Form,
  ICON_ADD_ROUND,
  ICON_DELETE_FOREVER,
  TextField,
  TextFieldProps,
} from '@squiz/sds';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React, { ChangeEvent, createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { COMPONENT_SERVICE_STRINGS } from '../../../../../../Constants/Strings.constants';
import {
  ComponentServicePatchComponentSetDetailsDto,
  EntityComponentSetComponentsForPatch,
  EntityComponentSetItem,
  getComponentService,
} from '../../../../../../Services/component';
import { createComponentsArray } from '../../utils/CreateComponentsArray/CreateComponentsArray';
import { ENVIRONMENT_VARIABLE_DATA_IDS, ENVIRONMENT_VARIABLE_FORM_IDS } from './EnvironmentVariableForm.constants';
import { EnvironmentVariableFormProps } from './EnvironmentVariableForm.props';
import { OverridesAccordion } from './elements/OverridesAccordion/OverridesAccordion';

/**
 * The form for editing component set environment variables.
 * @param {EnvironmentVariableFormProps} props Props passed to <EnvironmentVariableForm  />.
 * @returns {JSX.Element} The `<EnvironmentVariableForm />` component.
 */
export const EnvironmentVariableForm = (props: EnvironmentVariableFormProps): JSX.Element => {
  const { componentSetProps, updateComponentSetDetails } = props;
  const { setButtonKeys, setButtons, close } = props.dialogContentProps;

  const { t } = useTranslation();
  const [formError, setFormError] = useState<string>(``);
  const [componentSetItem] = useState<EntityComponentSetItem>(componentSetProps);
  const [showAddNewVariable, setShowAddNewVariable] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const canWriteEnvironmentVariables = hasPermission(PERMISSIONS.dxpComponents.COMPONENT_SET_ENVIRONMENT_WRITE);
  const disabled = !canWriteEnvironmentVariables;

  const variableMap = new Map(Object.entries(componentSetProps.environmentVariables || {}));
  // A map is used to maintain the order when adding a new variable
  const [envVarInputs, setEnvVarInputs] = useState<Map<string, string>>(variableMap);

  // Component service
  const componentService = getComponentService();

  const labels = useMemo(() => {
    return {
      add: t(COMPONENT_SERVICE_STRINGS.add),
      addVariable: t(COMPONENT_SERVICE_STRINGS.addVariable),
      cancel: t(COMPONENT_SERVICE_STRINGS.cancel),
      cannotWriteEnvironmentVariables: t(COMPONENT_SERVICE_STRINGS.cannotWriteEnvironmentVariables),
      duplicateEnvVarName: t(COMPONENT_SERVICE_STRINGS.duplicateEnvVarName),
      environmentVariablesDescription: t(COMPONENT_SERVICE_STRINGS.environmentVariablesDescription),
      failure: t(COMPONENT_SERVICE_STRINGS.apiError),
      invalidEnvVarName: t(COMPONENT_SERVICE_STRINGS.invalidEnvVarName),
      noEnvironmentVariables: t(COMPONENT_SERVICE_STRINGS.noEnvironmentVariables),
      required: t(COMPONENT_SERVICE_STRINGS.requiredField),
      save: t(COMPONENT_SERVICE_STRINGS.save),
      setEnvironmentVariables: t(COMPONENT_SERVICE_STRINGS.setEnvironmentVariables),
      variableName: t(COMPONENT_SERVICE_STRINGS.variableName),
    };
  }, [t]);

  // Form input refs
  const newEnvVarRefName = useRef<HTMLInputElement>(null);
  const newEnvVarNameRefProps = {
    errorMessage: fieldErrors[ENVIRONMENT_VARIABLE_FORM_IDS.new] || null,
    fieldLabel: labels.variableName,
    id: ENVIRONMENT_VARIABLE_FORM_IDS.new,
    name: 'new-variable',
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
  };

  const envVarRefs = useMemo(
    () => Array.from({ length: envVarInputs.size }).map(() => createRef<HTMLInputElement>()),
    [envVarInputs.size],
  );

  // Validators
  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  const validEnvVarName = useMemo(() => {
    return new EnvironmentVariableNameValidator<boolean>((): boolean => false);
  }, []);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...fieldErrors };
      inputs.forEach((input: HTMLInputElement): void => {
        const inputValue = input.value;
        const inputFieldId = input.getAttribute(`id`) as string;
        // Check that the input fields are valid
        if (!notEmptyValidator.validate(inputValue)) {
          nextErrors[inputFieldId] = labels.required;
        } else {
          delete nextErrors[inputFieldId];
        }
      });
      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, notEmptyValidator],
  );

  const validateEnvVarNameField = useCallback(
    (input: HTMLInputElement): void => {
      const error = labels.invalidEnvVarName;
      const nextErrors = { ...fieldErrors };
      const inputFieldId = input.getAttribute(`id`) as string;
      if (!validEnvVarName.doValidate(input.value)) {
        nextErrors[inputFieldId] = error;
      } else {
        delete nextErrors[inputFieldId];
      }
      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, validEnvVarName],
  );

  const checkForExistingEnvVar = useCallback(
    (input: HTMLInputElement): boolean => {
      if (envVarInputs.has(input.value)) {
        const error = labels.duplicateEnvVarName;
        const nextErrors = { ...fieldErrors };
        const inputFieldId = input.getAttribute(`id`) as string;
        nextErrors[inputFieldId] = error;
        setFieldErrors(nextErrors);
        return true;
      } else {
        return false;
      }
    },
    [envVarInputs, fieldErrors, labels.duplicateEnvVarName],
  );

  const handleShowAddInput = useCallback(() => {
    const nextErrors = { ...fieldErrors };
    nextErrors[ENVIRONMENT_VARIABLE_FORM_IDS.new] = null;
    setFieldErrors(nextErrors);
    setShowAddNewVariable(true);
  }, [fieldErrors]);

  const handleAddInput = useCallback(() => {
    const input = newEnvVarRefName.current as HTMLInputElement;

    if (!notEmptyValidator.validate(input.value)) {
      validateFields([input]);
    }

    if (checkForExistingEnvVar(input)) {
      return;
    }

    if (!validEnvVarName.doValidate(input.value)) {
      validateEnvVarNameField(input);
    } else {
      // We are using a map to preserve order.
      const nameValue = newEnvVarRefName.current?.value as string;
      envVarInputs.set(nameValue, '');
      setShowAddNewVariable(false);
    }
  }, [
    checkForExistingEnvVar,
    envVarInputs,
    notEmptyValidator,
    validateEnvVarNameField,
    validateFields,
    validEnvVarName,
  ]);

  const createTextFieldPropForEnvVar = (envVarKey: string): TextFieldProps => {
    return {
      errorMessage: fieldErrors[ENVIRONMENT_VARIABLE_FORM_IDS.value + envVarKey] || null,
      fieldLabel: envVarKey,
      id: ENVIRONMENT_VARIABLE_FORM_IDS.value + envVarKey,
      name: envVarKey,
      onChange: (event: ChangeEvent<HTMLInputElement>): void => {
        errorChecker(event);
      },
    };
  };

  // Delete function triggers re-render by creating a new map
  const deleteEnvironmentVariable = (variableName: string): void => {
    const updatedEnvVarInputs = new Map(envVarInputs);
    updatedEnvVarInputs.delete(variableName);
    setEnvVarInputs(updatedEnvVarInputs);
  };

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  const updateComponentsArray = useCallback((): Record<string, Array<EntityComponentSetComponentsForPatch>> => {
    return createComponentsArray(componentSetItem.components);
  }, [componentSetItem]);

  const createComponentSetDTO = useCallback(
    (
      componentsArray: Record<string, Array<EntityComponentSetComponentsForPatch>>,
      newEnvVarValues: {},
    ): ComponentServicePatchComponentSetDetailsDto => {
      const componentSetDto = {
        components: componentsArray,
        componentVersionRules: componentSetItem.componentVersionRules,
        description: componentSetItem.description,
        displayName: componentSetItem.displayName,
        environmentVariables: newEnvVarValues,
        headers: componentSetItem.headers,
        webpath: componentSetItem.webPath,
      };
      return componentSetDto;
    },
    [componentSetItem],
  );

  const getUpdatedEnvVars = useCallback((): Record<string, string> => {
    const updatedEnvVars = envVarRefs.reduce((acc, input) => {
      const varName = input.current?.name as string;
      return {
        ...acc,
        [varName]: input?.current?.value,
      };
    }, {});
    return updatedEnvVars;
  }, [envVarRefs]);

  const requestToPatchComponentSet = useCallback(
    (componentSetDto: ComponentServicePatchComponentSetDetailsDto): void => {
      lastValueFrom(componentService.patchComponentSetDetails(componentSetDto))
        .then(() => {
          updateComponentSetDetails();
          close();
          return true;
        })
        .catch((err) => {
          if (err.status === 400) {
            setFormError(err.response.message);
          } else {
            setFormError(labels.failure);
          }
          setSubmit(false);
        });
    },
    [close, componentService, labels, updateComponentSetDetails],
  );

  const getEnvVarInputs = useCallback((): Array<HTMLInputElement> => {
    const inputs = envVarRefs.map((input) => {
      return input.current as HTMLInputElement;
    });
    return inputs;
  }, [envVarRefs]);

  const getInvalidFields = useCallback(
    (inputs: Array<HTMLInputElement>): Array<HTMLInputElement> => {
      const invalidFields: Array<HTMLInputElement> = [];
      inputs.forEach((input) => {
        if (!notEmptyValidator.validate(input.value)) {
          invalidFields.push(input);
        }
      });
      return invalidFields;
    },
    [notEmptyValidator],
  );

  const submitHandler = useCallback(() => {
    // First get the current inputs
    const inputs = getEnvVarInputs();
    // Validate the required input fields
    const invalidFields = getInvalidFields(inputs);
    if (invalidFields.length > 0) {
      validateFields(invalidFields);
      setSubmit(false);
      return;
    }
    // Reset any form errors
    setFormError(``);
    const updatedEnvVars = getUpdatedEnvVars();
    // This formats the components array to match the expected components array in the management API
    const componentsArray = updateComponentsArray();
    // Construct the DTO for the create set request
    const componentSetDto = createComponentSetDTO(componentsArray, updatedEnvVars);
    // Send request to patch component set
    requestToPatchComponentSet(componentSetDto);
  }, [
    createComponentSetDTO,
    getEnvVarInputs,
    getInvalidFields,
    getUpdatedEnvVars,
    requestToPatchComponentSet,
    updateComponentsArray,
    validateFields,
  ]);

  useEffect(() => {
    // Dialog buttons
    const cancelButton = (
      <Button
        dataId={ENVIRONMENT_VARIABLE_DATA_IDS.cancel}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const saveButton = (
      <Button
        dataId={ENVIRONMENT_VARIABLE_DATA_IDS.save}
        format={BUTTON_FORMAT_STRONG}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.save}
        theme={BUTTON_THEME_PRIMARY}
      />
    );
    // Check for form submission
    if (isFormSubmitted) {
      setButtonKeys([`save`]);
      setButtons([saveButton]);
      submitHandler();
    } else if (disabled) {
      setButtonKeys([`cancel`]);
      setButtons([cancelButton]);
    } else {
      setButtonKeys([`cancel`, `save`]);
      setButtons([cancelButton, saveButton]);
    }
  }, [close, disabled, isFormSubmitted, labels, setButtonKeys, setButtons, submitHandler]);

  return (
    <div className="env-var-dialog">
      <div className="env-var-dialog__description">{labels.environmentVariablesDescription}</div>
      {!canWriteEnvironmentVariables && (
        <div className="env-var-dialog__warning">{labels.cannotWriteEnvironmentVariables}</div>
      )}
      <Form
        classes=""
        data-testid={ENVIRONMENT_VARIABLE_FORM_IDS.form}
        errorMessage={formError}
        legend={labels.environmentVariablesDescription}
        theme={FORM_THEME_LITE}
      >
        <span className="env-var-dialog__set-header">{labels.setEnvironmentVariables}</span>
        {/* Map the current sets environment variables to the text field where the name
         of the variable is the field name and the value is set to the current value of the input */}
        {envVarInputs.size === 0 && (
          <div className="env-var-dialog__form-control-container">{labels.noEnvironmentVariables}</div>
        )}
        {Array.from(envVarInputs).map(([key, value], index) => (
          <div className="env-var-dialog__form-control-container" key={key}>
            <TextField
              {...createTextFieldPropForEnvVar(key)}
              classes="env-var-dialog__form-control"
              data-id={ENVIRONMENT_VARIABLE_FORM_IDS.value}
              disabled={disabled}
              ref={envVarRefs[index]}
              value={value}
            ></TextField>
            {!disabled && (
              <Button
                classes="env-var-dialog__form-trash-icon"
                data-testid={ENVIRONMENT_VARIABLE_DATA_IDS.delete}
                format={BUTTON_FORMAT_TRANSPARENT}
                icon={ICON_DELETE_FOREVER}
                onClick={(): void => deleteEnvironmentVariable(key)}
                theme={BUTTON_THEME_DEFAULT}
              />
            )}
          </div>
        ))}
        {/* Button to show the add new variable div below */}
        <div>
          {!disabled && showAddNewVariable === false && (
            <Button
              classes="env-var-dialog__add-var-button"
              format={BUTTON_FORMAT_TRANSPARENT}
              icon={ICON_ADD_ROUND}
              isLite
              onClick={(): void => handleShowAddInput()}
              text={labels.addVariable}
              theme={BUTTON_THEME_DEFAULT}
            />
          )}
        </div>
        {/* The add new variable block */}
        {showAddNewVariable === true && (
          <div className="env-var-dialog__new-env-var-form">
            <TextField {...newEnvVarNameRefProps} ref={newEnvVarRefName}></TextField>
            <div className="env-var-dialog__new-env-var-form-buttons">
              <Button
                data-testid={ENVIRONMENT_VARIABLE_DATA_IDS.cancelNewVariable}
                format={BUTTON_FORMAT_SOFT}
                onClick={(): void => setShowAddNewVariable(false)}
                text={labels.cancel}
                theme={BUTTON_THEME_DEFAULT}
              />
              <Button
                format={BUTTON_FORMAT_STRONG}
                onClick={handleAddInput}
                text={labels.add}
                theme={BUTTON_THEME_PRIMARY}
              />
            </div>
          </div>
        )}
      </Form>
      <OverridesAccordion componentSet={componentSetProps} />
    </div>
  );
};
